import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './hooks/useAuth';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import LoadingSpinner from './components/LoadingSpinner';

// 懒加载页面
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const HomeworkListPage = lazy(() => import('./pages/HomeworkListPage'));
const HomeworkDetailPage = lazy(() => import('./pages/HomeworkDetailPage'));
const FeedbackListPage = lazy(() => import('./pages/FeedbackListPage'));
const AttendanceListPage = lazy(() => import('./pages/AttendanceListPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const TeacherWorkbench = lazy(() => import('./pages/TeacherWorkbench'));

// 管理页面
const InviteCodesPage = lazy(() => import('./pages/admin/InviteCodesPage'));
const CreateHomeworkPage = lazy(() => import('./pages/admin/CreateHomeworkPage'));
const CreateFeedbackPage = lazy(() => import('./pages/admin/CreateFeedbackPage'));
const RecordAttendancePage = lazy(() => import('./pages/admin/RecordAttendancePage'));
const StudentManagementPage = lazy(() => import('./pages/admin/StudentManagementPage'));

// 超级管理员页面
const UserManagementPage = lazy(() => import('./pages/admin/UserManagementPage'));
const EditHomeworkPage = lazy(() => import('./pages/admin/EditHomeworkPage'));

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* 公开页面 */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />

            {/* 需要登录的页面 */}
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="homework" element={<HomeworkListPage />} />
              <Route path="homework/:id" element={<HomeworkDetailPage />} />
              <Route path="feedback" element={<FeedbackListPage />} />
              <Route path="attendance" element={<AttendanceListPage />} />
              <Route path="calendar" element={<CalendarPage />} />

              {/* 老师/管理员页面 */}
              <Route
                path="admin/invite-codes"
                element={
                  <ProtectedRoute allowedRoles={['teacher', 'superadmin']}>
                    <InviteCodesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/homework/new"
                element={
                  <ProtectedRoute allowedRoles={['teacher', 'admin', 'superadmin']}>
                    <CreateHomeworkPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/feedback/new"
                element={
                  <ProtectedRoute allowedRoles={['teacher', 'admin', 'superadmin']}>
                    <CreateFeedbackPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/attendance/record"
                element={
                  <ProtectedRoute allowedRoles={['teacher', 'admin', 'superadmin']}>
                    <RecordAttendancePage />
                  </ProtectedRoute>
                }
              />

              {/* 上课看板（teacher / admin / superadmin） */}
              <Route
                path="teacher/workbench"
                element={
                  <ProtectedRoute allowedRoles={['teacher', 'admin', 'superadmin']}>
                    <TeacherWorkbench />
                  </ProtectedRoute>
                }
              />

              {/* 学生管理（admin + superadmin） */}
              <Route
                path="admin/students"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                    <StudentManagementPage />
                  </ProtectedRoute>
                }
              />

              {/* 超级管理员专属页面 */}
              <Route
                path="admin/users"
                element={
                  <ProtectedRoute allowedRoles={['superadmin']}>
                    <UserManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin/homework/:id/edit"
                element={
                  <ProtectedRoute allowedRoles={['superadmin', 'admin']}>
                    <EditHomeworkPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* 404 */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}
